import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth, type Papel } from './contexts/AuthContext.tsx';
import { TemaProvider } from './contexts/ThemeContext.tsx';
import AppShell from './components/layout/AppShell.tsx';
import Login from './pages/Login.tsx';
import Inicio from './pages/Inicio.tsx';
import Vendas from './pages/Vendas.tsx';
import Carteira from './pages/financeiro/Carteira.tsx';
import Inadimplentes from './pages/financeiro/Inadimplentes.tsx';
import Revistoria from './pages/financeiro/Revistoria.tsx';
import Equipe from './pages/Equipe.tsx';
import Usuarios from './pages/Usuarios.tsx';
import Perfil from './pages/Perfil.tsx';
import RastreamentoLayout from './pages/rastreamento/Layout.tsx';
import Controle from './pages/rastreamento/Controle.tsx';
import Planejamento from './pages/rastreamento/Planejamento.tsx';

// O mapa traz o MapLibre (~200 KB comprimido): só baixa quem abre a aba.
const Mapa = lazy(() => import('./pages/rastreamento/Mapa.tsx'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // O consultor abre isto no 4G em campo: refazer a busca a cada foco de
      // janela gastaria dados dele sem entregar nada — o espelho atualiza em
      // ciclos de minutos, não de segundos.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function Protegida({ children, papeis }: { children: React.ReactNode; papeis?: Papel[] }) {
  const { usuario, carregando, pode } = useAuth();

  if (carregando) {
    return (
      <div className="grid min-h-dvh place-items-center bg-[var(--bg)]">
        <div className="size-5 animate-spin rounded-full border-2 border-[var(--border-strong)] border-t-[var(--fg)]" />
      </div>
    );
  }
  if (!usuario) return <Navigate to="/entrar" replace />;
  if (papeis && !pode(...papeis)) return <Navigate to="/inicio" replace />;

  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TemaProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/entrar" element={<Login />} />

              <Route
                element={
                  <Protegida>
                    <AppShell />
                  </Protegida>
                }
              >
                <Route path="/inicio" element={<Inicio />} />
                <Route path="/vendas" element={<Vendas />} />
                <Route path="/financeiro" element={<Outlet />}>
                  <Route
                    index
                    element={
                      <Protegida papeis={['admin', 'diretor', 'gerente_regional', 'financeiro']}>
                        <Carteira />
                      </Protegida>
                    }
                  />
                  {/* O consultor entra para ver a própria carteira — a API
                      recorta por ele, ignorando o filtro que vier. */}
                  <Route
                    path="inadimplentes"
                    element={
                      <Protegida
                        papeis={['admin', 'diretor', 'gerente_regional', 'financeiro', 'consultor']}
                      >
                        <Inadimplentes />
                      </Protegida>
                    }
                  />
                  {/* Pós-vendas entra: é quem agenda a vistoria. */}
                  <Route
                    path="revistoria"
                    element={
                      <Protegida
                        papeis={[
                          'admin',
                          'diretor',
                          'gerente_regional',
                          'financeiro',
                          'pos_vendas',
                          'consultor',
                        ]}
                      >
                        <Revistoria />
                      </Protegida>
                    }
                  />
                </Route>
                <Route
                  path="/rastreamento"
                  element={
                    <Protegida papeis={['admin', 'diretor', 'rastreamento']}>
                      <RastreamentoLayout />
                    </Protegida>
                  }
                >
                  <Route index element={<Controle />} />
                  <Route path="planejamento" element={<Planejamento />} />
                  <Route
                    path="mapa"
                    element={
                      <Suspense
                        fallback={<div className="carregando h-[520px] rounded-[var(--r-lg)]" />}
                      >
                        <Mapa />
                      </Suspense>
                    }
                  />
                </Route>
                {/* Metas virou uma folha dentro de Vendas. */}
                <Route path="/metas" element={<Navigate to="/vendas" replace />} />
                <Route
                  path="/equipe"
                  element={
                    <Protegida papeis={['admin', 'diretor', 'gerente_regional', 'financeiro']}>
                      <Equipe />
                    </Protegida>
                  }
                />
                <Route
                  path="/usuarios"
                  element={
                    <Protegida papeis={['admin', 'diretor']}>
                      <Usuarios />
                    </Protegida>
                  }
                />
                <Route path="/perfil" element={<Perfil />} />
              </Route>

              <Route path="*" element={<Navigate to="/inicio" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TemaProvider>
    </QueryClientProvider>
  );
}
